import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import LeadershipPage from './pages/public/LeadershipPage';
import StructurePage from './pages/public/StructurePage';
import MembershipPage from './pages/public/MembershipPage';
import NewsPage from './pages/public/NewsPage';
import EventsPage from './pages/public/EventsPage';
import GalleryPage from './pages/public/GalleryPage';
import DocumentsPage from './pages/public/DocumentsPage';
import ContactPage from './pages/public/ContactPage';
import SupportPage from './pages/public/SupportPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';

// Admin Pages
import DashboardPage from './pages/admin/DashboardPage';
import MembersPage from './pages/admin/MembersPage';
import ApplicantsPage from './pages/admin/ApplicantsPage';
import UnitsPage from './pages/admin/UnitsPage';
import ProjectsPage from './pages/admin/ProjectsPage';
import TasksPage from './pages/admin/TasksPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import GalleryManagementPage from './pages/admin/GalleryManagementPage';
import FinancePage from './pages/admin/FinancePage';
import GovernancePage from './pages/admin/GovernancePage';
import InquiriesPage from './pages/admin/InquiriesPage';
import { 
  AppointmentsPage, 
  CommunicationsPage, 
  ContributionsPage, 
  AuditLogsPage, 
  ReportsPage, 
  UsersPage 
} from './pages/admin/AdditionalPages';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Website Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/leadership" element={<LeadershipPage />} />
      <Route path="/structure" element={<StructurePage />} />
      <Route path="/membership" element={<MembershipPage />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/documents" element={<DocumentsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/support" element={<SupportPage />} />

      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/admin" replace /> : <LoginPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><DashboardPage /></ProtectedRoute>} />
      <Route path="/admin/members" element={<ProtectedRoute adminOnly><MembersPage /></ProtectedRoute>} />
      <Route path="/admin/applicants" element={<ProtectedRoute adminOnly><ApplicantsPage /></ProtectedRoute>} />
      <Route path="/admin/units" element={<ProtectedRoute adminOnly><UnitsPage /></ProtectedRoute>} />
      <Route path="/admin/projects" element={<ProtectedRoute adminOnly><ProjectsPage /></ProtectedRoute>} />
      <Route path="/admin/tasks" element={<ProtectedRoute adminOnly><TasksPage /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute adminOnly><AppointmentsPage /></ProtectedRoute>} />
      <Route path="/admin/events" element={<ProtectedRoute adminOnly><AdminEventsPage /></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute adminOnly><AdminDocumentsPage /></ProtectedRoute>} />
      <Route path="/admin/gallery" element={<ProtectedRoute adminOnly><GalleryManagementPage /></ProtectedRoute>} />
      <Route path="/admin/communications" element={<ProtectedRoute adminOnly><CommunicationsPage /></ProtectedRoute>} />
      <Route path="/admin/inquiries" element={<ProtectedRoute adminOnly><InquiriesPage /></ProtectedRoute>} />
      <Route path="/admin/governance" element={<ProtectedRoute adminOnly><GovernancePage /></ProtectedRoute>} />
      <Route path="/admin/finance" element={<ProtectedRoute adminOnly><FinancePage /></ProtectedRoute>} />
      <Route path="/admin/contributions" element={<ProtectedRoute adminOnly><ContributionsPage /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute adminOnly><ReportsPage /></ProtectedRoute>} />
      <Route path="/admin/audit-logs" element={<ProtectedRoute adminOnly><AuditLogsPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute adminOnly><UsersPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
